import { Box, Button, Divider, LinearProgress, LinearProgressProps, Typography } from "@mui/material";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import moment from "moment";
import clsx from "clsx";
import React, { useState } from "react";
import RadioCustom from "../../../../components/radio/RadioCustom";
import PreviewImage from "../../../../components/preview-image/PreviewImage";
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { useTranslation } from "react-i18next";

type pollVotePopularBox = {
    data: { title: string, group_name: string, total_vote: string, start_date: string, end_date: string }[];
}

export const PollVotePopularBox: React.FC<pollVotePopularBox> = ({ data }) => {
    const { t } = useTranslation();
    return (
        <Box className="flex flex-col">
            {data?.map((list, index) => {
                return (
                    <React.Fragment key={index}>
                        <Box className="w-full flex flex-col md:flex-row justify-start items-center gap-4" >
                            <Box className="w-full flex justify-center items-center max-w-[70px]">
                                <Typography fontWeight={600} sx={{ color: "#4A4A4A" }} fontSize={{ xs: "40px", xl: "64px" }}>
                                    {index + 1}
                                </Typography>
                            </Box>
                            <Box className="w-full flex justify-start items-start flex-col gap-2">
                                <Typography noWrap variant="h6">
                                    {list.title}
                                </Typography>
                                <Box className="flex gap-5">
                                    <Typography>
                                        {t("HOME_PAGE_CATEGORY")} : {list.group_name}
                                    </Typography>
                                    <Typography>
                                        {t("HOME_PAGE_NUMBER_OF_VOTERS")} : {list.total_vote || "0"}
                                    </Typography>
                                </Box>
                                <Typography className="flex gap-2 justify-center items-center">
                                    <CalendarMonthIcon />
                                    {`${moment(list.start_date).isValid() ? moment(list.start_date).format("DD/MM/YY") : t("HOME_PAGE_NO_INFORMATION")} - ${moment(list.end_date).isValid() ? moment(list.end_date).format("DD/MM/YY") : t("HOME_PAGE_NO_INFORMATION")}`}
                                </Typography>
                            </Box>
                        </Box>
                        {(data?.length - 1) !== index && (
                            <Divider sx={{ my: 2 }} />
                        )}
                    </React.Fragment>
                )
            })}
        </Box>
    )
}


type pollVoteCardDataList = {
    id_form_poll?: number | null;
    id_form_poll_choice_select?: number | null;
    id_form_poll_choice?: any;
    img_src: string;
    title: string;
    group_name: string,
    total_vote: string,
    start_date: string,
    end_date: string,
    auth_required: boolean,
    form_poll_choice?: any[]
}
type pollVoteCardDataProps = {
    show_one?: pollVoteCardDataList;
    onChange?: (id_form_poll: number, id_form_poll_choice: number, auth_required: boolean) => void;
    handleChangeFormPoll?: (d_form_poll: number, id_form_poll_choice: number) => void;
    setData?: React.Dispatch<React.SetStateAction<any[]>>;
    data: pollVoteCardDataList[]
}

export function LinearProgressWithLabel(props: LinearProgressProps & { value: number, choice_name: string, total_count: number, choice_data: any[], onChange: (id_form_poll_choice: number) => void }) {
    const default_id_form_poll_choice = props.choice_data?.find((x) => x?.is_user_selected)?.id_form_poll_choice;
    const [id_form_poll_choice, set_id_form_poll_choice] = useState<string>(default_id_form_poll_choice ? String(default_id_form_poll_choice) : "");

    return (
        <Box className="flex justify-start flex-col items-start w-full gap-2">
            <Typography sx={{ color: '#828282' }}>{props.choice_name}</Typography>
            <Box sx={(theme) => ({
                display: 'flex', alignItems: 'center', width: '100%', gap: 4,
                [theme.breakpoints.down('md')]: {
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                }
            })}>
                <Box className="flex flex-col gap-4 w-full">
                    {props.choice_data?.map((list, i) => {
                        return (
                            <Box key={i} className="flex flex-col w-full">
                                <Box className="w-full">
                                    <RadioCustom
                                        onChange={(e) => {
                                            set_id_form_poll_choice(e.target.value)
                                            props.onChange?.(Number(e.target.value));
                                        }}
                                        value={id_form_poll_choice}
                                        between
                                        dataList={[
                                            {
                                                label: list?.form_poll_choice,
                                                value: list?.id_form_poll_choice,
                                                renderAfter: (
                                                    <Box>
                                                        {list?.percent || 0} %
                                                    </Box>
                                                )
                                            }
                                        ]}
                                    />
                                </Box>
                                <Box sx={{ width: '100%', borderRadius: "30px" }}>
                                    <LinearProgress sx={{
                                        height: ".5rem",
                                        borderRadius: "30px",
                                        backgroundColor: "#F2F4F7",
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: '#CFA56D',
                                            borderRadius: '30px',
                                        },
                                    }} variant="determinate" value={list?.percent || 0} />
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    );
}
export const PollVoteCard: React.FC<pollVoteCardDataProps> = ({ show_one, data, setData, onChange, handleChangeFormPoll }) => {
    const { t } = useTranslation();
    return (
        <div>
            {show_one ? (
                <Box className={clsx('min-h-full bg-[#FFFFFF] shadow-xl h-full flex flex-col md:flex-row gap-4 rounded-[16px] overflow-hidden')}>
                    <Box className="w-full h-fill md:max-w-[387px] overflow-hidden">
                        <PreviewImage
                            disable_show={false}
                            image_url={show_one.img_src}
                            disabled_max_width
                            render_image={
                                <img
                                    style={{ width: "100%", height: '100%', objectFit: "cover" }}
                                    width={"100%"}
                                    height={"100%"}
                                    className="object-cover h-full w-full" src={show_one.img_src} />
                            }
                        />
                    </Box>
                    <Box className="w-full flex flex-col">
                        <Box className="p-6 flex flex-col">
                            <Typography noWrap variant="h6">
                                {show_one.title}
                            </Typography>
                            <Box className="w-full flex gap-5">
                                <Typography>
                                    {t("HOME_PAGE_CATEGORY")} : {show_one.group_name}
                                </Typography>
                                <Typography>
                                    {t("HOME_PAGE_NUMBER_OF_VOTERS")} : {show_one.total_vote || "0"}
                                </Typography>
                            </Box>
                            <Typography className="flex gap-2 justify-start items-center">
                                <CalendarMonthIcon />
                                {`${moment(show_one.start_date).isValid() ? moment(show_one.start_date).format("DD/MM/YY") : t("ไม่มีข้อมูล")} - ${moment(show_one.end_date).isValid() ? moment(show_one.end_date).format("DD/MM/YY") : t("ไม่มีข้อมูล")}`}
                            </Typography>
                        </Box>
                        <Box className={clsx('w-full p-6 flex justify-between h-[354px] items-start flex-col gap-2', {
                            'overflow-auto': show_one?.form_poll_choice && show_one?.form_poll_choice?.length > 4
                        })}>
                            <Box className="w-full flex-col justify-start gap-4 items-start">
                                <LinearProgressWithLabel
                                    choice_name=""
                                    total_count={0}
                                    value={0}
                                    onChange={(id_form_poll_choice) => {
                                        show_one?.id_form_poll && handleChangeFormPoll?.(show_one?.id_form_poll, id_form_poll_choice as number)
                                        // console.log(id_form_poll_choice, 'id')
                                        // setData?.((prev) => prev?.map((list, index) => {
                                        //     return i === index ? {
                                        //         ...list,
                                        //         id_form_poll_choice_select: +id
                                        //     } : list
                                        // }))
                                    }}
                                    choice_data={(show_one?.form_poll_choice && show_one?.form_poll_choice?.length > 0) ?
                                        show_one?.form_poll_choice
                                        : []}
                                />

                            </Box>
                        </Box>
                        <Box px={4} py={4}>
                            <Button
                                onClick={() => show_one?.id_form_poll && show_one?.id_form_poll_choice_select && onChange?.(show_one?.id_form_poll, show_one?.id_form_poll_choice_select, show_one?.auth_required)}
                                disabled={!show_one?.id_form_poll_choice_select}
                                startIcon={<CheckBoxOutlinedIcon />}
                                sx={{ maxWidth: "100%", borderRadius: "8px" }}
                                color="success"
                                fullWidth
                                variant="contained">
                                {t("HOME_PAGE_VOTE")}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box sx={{
                    display: 'grid',
                    placeItems: 'center',
                    gap: "32px",
                    gridTemplateColumns: "repeat(auto-fill, minmax(406px, 1fr))"
                }}>
                    {data?.map((list, i) => {
                        return (
                            <Box data-aos="fade-up"
                                data-aos-duration="3000" key={i} className={clsx('min-h-[754px] shadow-xl rounded-[16px] overflow-hidden')}>
                                <Box className="w-full h-full overflow-hidden max-h-[300px]">
                                    <PreviewImage
                                        disable_show={false}
                                        image_url={list.img_src}
                                        disabled_max_width
                                        render_image={
                                            <img
                                                style={{ width: "100%", height: '100%', objectFit: "cover" }}
                                                width={"100%"}
                                                height={"100%"}
                                                className="object-cover h-full w-full" src={list.img_src} alt={`image-${i}`} />
                                        }
                                    />
                                </Box>
                                <Box className="p-6">
                                    <Typography noWrap variant="h6">
                                        {list.title}
                                    </Typography>
                                    <Box className="flex gap-5">
                                        <Typography>
                                            {t("HOME_PAGE_CATEGORY")} : {list.group_name}
                                        </Typography>
                                        <Typography>
                                            {t("HOME_PAGE_NUMBER_OF_VOTERS")} : {list.total_vote || "0"}
                                        </Typography>
                                    </Box>
                                    <Typography className="flex gap-2 justify-start items-center">
                                        <CalendarMonthIcon />
                                        {`${moment(list.start_date).isValid() ? moment(list.start_date).format("DD/MM/YY") : t("ไม่มีข้อมูล")} - ${moment(list.end_date).isValid() ? moment(list.end_date).format("DD/MM/YY") : t("ไม่มีข้อมูล")}`}
                                    </Typography>
                                </Box>
                                <Box className={clsx('w-full p-6 flex justify-between h-[354px] items-start flex-col gap-2', {
                                    'overflow-auto': list?.form_poll_choice && list?.form_poll_choice?.length > 4
                                })}>
                                    <Box className="w-full flex-col justify-start gap-4 items-start">
                                        <LinearProgressWithLabel
                                            choice_name=""
                                            total_count={0}
                                            value={0}
                                            onChange={(id) => {
                                                setData?.((prev) => prev?.map((list, index) => {
                                                    return i === index ? {
                                                        ...list,
                                                        id_form_poll_choice_select: +id
                                                    } : list
                                                }))
                                            }}
                                            choice_data={(list?.form_poll_choice && list?.form_poll_choice?.length > 0) ?
                                                list?.form_poll_choice
                                                : []}
                                        />

                                    </Box>
                                </Box>
                                <Box px={4} py={4}>
                                    <Button
                                        onClick={() => list?.id_form_poll && list?.id_form_poll_choice_select && onChange?.(list?.id_form_poll, list?.id_form_poll_choice_select, list?.auth_required)}
                                        disabled={!list?.id_form_poll_choice_select}
                                        startIcon={<CheckBoxOutlinedIcon />}
                                        sx={{ maxWidth: "100%", borderRadius: "8px" }}
                                        color="success"
                                        fullWidth
                                        variant="contained">
                                        {t("HOME_PAGE_VOTE")}
                                    </Button>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            )}
        </div>
    )
}

